import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { openWhatsApp, openEmail, openCall } from '@/lib/contact';
import { Plus, Search, Phone, Mail, MessageSquare, ChevronRight, ArrowRight, Trash2, Edit, Filter, FileText, Copy, Video, Users, Clock } from 'lucide-react';
import { useConfirm } from '@/components/ConfirmDialog';
import ProposalBuilder from '@/components/ProposalBuilder';
import { useAuth } from '@/contexts/AuthContext';
import { isOwner } from '@/lib/perms';
import InlineEdit from '@/components/InlineEdit';

const statusColors = {
  Contacted: 'bg-[#E8E6E1] text-[#2C2C2E]',
  Consultation: 'bg-[#C67D5B]/10 text-[#C67D5B]',
  Proposal: 'bg-[#3A8C8C]/15 text-[#3A8C8C]',
  Won: 'bg-[#7DA68A]/10 text-[#7DA68A]',
  Lost: 'bg-[#C4727F]/10 text-[#C4727F]',
};
const sourceOptions = ['Reference/WOM', 'Ads', 'Vendor/Agency', 'Other'];
const typeOptions = ['Residential', 'Commercial', 'Retail', 'Other'];
const statusPipeline = ['Contacted', 'Consultation', 'Proposal', 'Won', 'Lost'];
const LOSS_REASONS = [
  'Budget Too High', 'Chose Competitor', 'Project Postponed', 'Not Interested',
  'No Response', 'Scope Mismatch', 'Timeline Issue', 'Internal Decision',
  'Duplicate Lead', 'Other',
];

function formatINR(n) { if (!n) return '0'; const num = Number(n); if (num >= 100000) return `${(num/100000).toFixed(1)}L`; return num.toLocaleString('en-IN'); }

// ── Lead follow-ups (interaction tracking) ──
const FU_CHANNELS = [
  { value: 'Call', label: 'Call', Icon: Phone },
  { value: 'In Person', label: 'In Person', Icon: Users },
  { value: 'Virtual Meeting', label: 'Virtual Meeting', Icon: Video },
  { value: 'WhatsApp', label: 'WhatsApp', Icon: MessageSquare },
  { value: 'Email', label: 'Email', Icon: Mail },
  { value: 'Other', label: 'Other', Icon: MessageSquare },
];
const FU_OUTCOMES = ['Follow up', 'Interested', 'Details Shared', 'Meeting Scheduled', 'Proposal Shared', 'Negotiation', 'No Response', 'Not Interested', 'Other'];
const fuOutcomeColor = (o) => ({
  'Interested': 'bg-[#7DA68A]/15 text-[#7DA68A]',
  'Meeting Scheduled': 'bg-[#3A8C8C]/15 text-[#3A8C8C]',
  'Proposal Shared': 'bg-[#3A8C8C]/15 text-[#3A8C8C]',
  'Details Shared': 'bg-[#3A8C8C]/10 text-[#3A8C8C]',
  'Negotiation': 'bg-[#C67D5B]/12 text-[#C67D5B]',
  'Follow up': 'bg-[#C67D5B]/12 text-[#C67D5B]',
  'No Response': 'bg-[#E8E6E1] text-[#6B7280]',
  'Not Interested': 'bg-[#C4727F]/12 text-[#C4727F]',
}[o] || 'bg-[#E8E6E1] text-[#6B7280]');
const fuChannelIcon = (ch) => (FU_CHANNELS.find(c => c.value === ch) || FU_CHANNELS[FU_CHANNELS.length - 1]).Icon;
function fuFormatDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterLossReason, setFilterLossReason] = useState('all');
  const [searchQ, setSearchQ] = useState('');
  const [lostModal, setLostModal] = useState(null); // { lead, lost_reason, lost_notes }
  const [form, setForm] = useState({ client_name: '', email: '', phone: '', source: 'Reference/WOM', project_type: 'Residential', area_location: '', budget: 0, status: 'Contacted', notes: '' });

  const loadLeads = async () => {
    try {
      const res = await api.get('/leads');
      // Migrate any legacy "Qualifies" leads to "Contacted" (stage removed).
      const legacy = res.data.filter(l => l.status === 'Qualifies');
      if (legacy.length) {
        await Promise.all(legacy.map(l => api.put(`/leads/${l.id}`, { status: 'Contacted' })));
      }
      const mapped = res.data.map(l => l.status === 'Qualifies' ? { ...l, status: 'Contacted' } : l);
      setLeads(mapped);
      // Keep the open lead detail in sync (e.g. after a proposal add/delete/revise).
      setSelectedLead(prev => (prev ? (mapped.find(l => l.id === prev.id) || prev) : prev));
    } catch { toast.error('Failed to load leads'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadLeads(); }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const leadId = searchParams.get('lead');
    if (leadId && leads.length) {
      const found = leads.find(l => l.id === leadId);
      if (found) {
        setSelectedLead(found);
        const next = new URLSearchParams(searchParams);
        next.delete('lead');
        setSearchParams(next, { replace: true });
      }
    }
  }, [searchParams, leads]);

  const handleCreate = async () => {
    try {
      await api.post('/leads', form);
      toast.success('Lead created');
      setShowCreate(false);
      setForm({ client_name: '', email: '', phone: '', source: 'Reference/WOM', project_type: 'Residential', area_location: '', budget: 0, status: 'Contacted', notes: '' });
      loadLeads();
    } catch { toast.error('Failed to create lead'); }
  };

  const updateStatus = async (id, status) => {
    if (status === 'Lost') {
      const lead = leads.find(l => l.id === id);
      setLostModal({ lead, lost_reason: '', lost_notes: '' });
      return;
    }
    try {
      await api.put(`/leads/${id}`, { status });
      toast.success(`Status updated to ${status}`);
      loadLeads();
      if (selectedLead?.id === id) setSelectedLead(prev => ({...prev, status}));
    } catch { toast.error('Update failed'); }
  };

  const submitMarkLost = async () => {
    if (!lostModal?.lost_reason) {
      toast.error('Loss reason is required');
      return;
    }
    try {
      const res = await api.post(`/leads/${lostModal.lead.id}/mark-lost`, {
        lost_reason: lostModal.lost_reason,
        lost_notes: lostModal.lost_notes,
      });
      toast.success('Lead marked as Lost');
      setLostModal(null);
      loadLeads();
      if (selectedLead?.id === lostModal.lead.id) setSelectedLead(res.data);
    } catch { toast.error('Failed to mark as Lost'); }
  };

  const convertToProject = async (id) => {
    try {
      const res = await api.post(`/leads/${id}/convert`);
      toast.success('Lead converted to project!');
      loadLeads();
      setSelectedLead(null);
    } catch { toast.error('Conversion failed'); }
  };

  const deleteLead = async (id) => {
    try {
      await api.delete(`/leads/${id}`);
      toast.success('Lead deleted');
      setSelectedLead(null);
      loadLeads();
    } catch { toast.error('Delete failed'); }
  };

  const filtered = leads.filter(l => {
    if (filterStatus !== 'all' && l.status !== filterStatus) return false;
    if (filterStatus === 'Lost' && filterLossReason !== 'all' && (l.lost_reason || 'Reason Not Specified') !== filterLossReason) return false;
    if (searchQ && !l.client_name.toLowerCase().includes(searchQ.toLowerCase())) return false;
    return true;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#3A8C8C] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="leads-page">
      {/* Header */}
      <div className="section-heading-row flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-['Manrope'] font-bold">Leads</h1>
          <p className="text-sm text-[#6B7280] mt-0.5">{leads.length} total leads</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <button data-testid="create-lead-btn" className="bg-[#3A8C8C] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#2E7A7A] transition-colors flex items-center gap-2 active:scale-95">
              <Plus size={16} strokeWidth={1.5} /> New Lead
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-['Manrope']">New Lead</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-[#6B7280] uppercase tracking-wide">Client Name</Label><Input data-testid="lead-name-input" value={form.client_name} onChange={e => setForm({...form, client_name: e.target.value})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
                <div><Label className="text-xs text-[#6B7280] uppercase tracking-wide">Phone</Label><Input data-testid="lead-phone-input" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
              </div>
              <div><Label className="text-xs text-[#6B7280] uppercase tracking-wide">Email</Label><Input data-testid="lead-email-input" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-[#6B7280] uppercase tracking-wide">Source</Label>
                  <Select value={form.source} onValueChange={v => setForm({...form, source: v})}><SelectTrigger className="mt-1 h-10 rounded-xl border-[#D4D0C8]" data-testid="lead-source-select"><SelectValue /></SelectTrigger><SelectContent>{sourceOptions.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label className="text-xs text-[#6B7280] uppercase tracking-wide">Project Type</Label>
                  <Select value={form.project_type} onValueChange={v => setForm({...form, project_type: v})}><SelectTrigger className="mt-1 h-10 rounded-xl border-[#D4D0C8]" data-testid="lead-type-select"><SelectValue /></SelectTrigger><SelectContent>{typeOptions.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-[#6B7280] uppercase tracking-wide">Location</Label><Input value={form.area_location} onChange={e => setForm({...form, area_location: e.target.value})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
                <div><Label className="text-xs text-[#6B7280] uppercase tracking-wide">Budget (INR)</Label><Input type="number" data-testid="lead-budget-input" value={form.budget} onChange={e => setForm({...form, budget: Number(e.target.value)})} className="mt-1 h-10 rounded-xl border-[#D4D0C8]" /></div>
              </div>
              <div><Label className="text-xs text-[#6B7280] uppercase tracking-wide">Notes</Label><textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="mt-1 w-full h-20 px-4 py-2 bg-white border border-[#D4D0C8] rounded-xl text-sm focus:ring-[#3A8C8C]/20 focus:border-[#3A8C8C] outline-none resize-none" /></div>
              <button onClick={handleCreate} data-testid="submit-lead-btn" className="w-full h-10 bg-[#3A8C8C] text-white rounded-full font-medium text-sm hover:bg-[#2E7A7A] transition-colors">Create Lead</button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pipeline visualization */}
      <div className="flex gap-2 overflow-x-auto pb-2" data-testid="lead-pipeline">
        {statusPipeline.map(s => {
          const count = leads.filter(l => l.status === s).length;
          return (
            <button key={s} onClick={() => setFilterStatus(filterStatus === s ? 'all' : s)} className={`px-4 py-2 rounded-full text-xs font-medium transition-all whitespace-nowrap ${filterStatus === s ? 'bg-[#3A8C8C] text-white' : statusColors[s] || 'bg-[#E8E6E1] text-[#2C2C2E]'}`}>
              {s} ({count})
            </button>
          );
        })}
        <button onClick={() => setFilterStatus('all')} className={`px-4 py-2 rounded-full text-xs font-medium transition-all ${filterStatus === 'all' ? 'bg-[#2C2C2E] text-white' : 'bg-[#E8E6E1] text-[#2C2C2E]'}`}>
          All ({leads.length})
        </button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-sm">
          <Search size={16} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <Input placeholder="Search leads..." value={searchQ} onChange={e => setSearchQ(e.target.value)} className="pl-9 h-9 rounded-xl border-[#E8E6E1] bg-white" data-testid="leads-search" />
        </div>
        {filterStatus === 'Lost' && (
          <div className="flex items-center gap-2" data-testid="lost-reason-filter">
            <Filter size={14} className="text-[#6B7280]" />
            <Select value={filterLossReason} onValueChange={setFilterLossReason}>
              <SelectTrigger className="h-9 rounded-xl border-[#E8E6E1] bg-white text-xs w-52" data-testid="lost-reason-filter-trigger">
                <SelectValue placeholder="Filter by loss reason" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All loss reasons</SelectItem>
                {LOSS_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                <SelectItem value="Reason Not Specified">Reason Not Specified</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Lead detail or table */}
      {selectedLead ? (
        <LeadDetail lead={selectedLead} onClose={() => setSelectedLead(null)} onStatusChange={updateStatus} onConvert={convertToProject} onDelete={deleteLead} onRefresh={loadLeads} />
      ) : (
        <div className="glass-card rounded-2xl overflow-hidden" data-testid="leads-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#FAFAF8]">
                <TableHead className="text-xs font-medium text-[#6B7280] uppercase">Client</TableHead>
                <TableHead className="text-xs font-medium text-[#6B7280] uppercase">Source</TableHead>
                <TableHead className="text-xs font-medium text-[#6B7280] uppercase">Type</TableHead>
                <TableHead className="text-xs font-medium text-[#6B7280] uppercase">Budget</TableHead>
                <TableHead className="text-xs font-medium text-[#6B7280] uppercase">Status</TableHead>
                <TableHead className="text-xs font-medium text-[#6B7280] uppercase">Location</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(lead => (
                <TableRow key={lead.id} onClick={() => setSelectedLead(lead)} className="cursor-pointer hover:bg-[#FAFAF8] transition-colors" data-testid={`lead-row-${lead.id}`}>
                  <TableCell>
                    <div><span className="text-sm font-medium text-[#2C2C2E]">{lead.client_name}</span></div>
                    <div className="text-xs text-[#6B7280]">{lead.email}</div>
                  </TableCell>
                  <TableCell className="text-sm text-[#6B7280]">{lead.source}</TableCell>
                  <TableCell><Badge className="bg-[#E8E6E1] text-[#2C2C2E] text-[10px] font-normal">{lead.project_type}</Badge></TableCell>
                  <TableCell className="text-sm text-[#2C2C2E] font-medium">{formatINR(lead.budget)}</TableCell>
                  <TableCell>
                    <Badge className={`text-[10px] ${statusColors[lead.status] || ''}`}>{lead.status}</Badge>
                    {lead.status === 'Lost' && (
                      <div className="text-[10px] text-[#C4727F] mt-0.5">{lead.lost_reason || 'Reason Not Specified'}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-[#6B7280]">{lead.area_location}</TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-sm text-[#6B7280] py-8">No leads found</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Mark-as-Lost dialog */}
      <Dialog open={!!lostModal} onOpenChange={(o) => !o && setLostModal(null)}>
        <DialogContent className="max-w-md" data-testid="lost-reason-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-[#C4727F]">Mark Lead as Lost</DialogTitle>
          </DialogHeader>
          {lostModal && (
            <div className="space-y-4 mt-2">
              <p className="text-xs text-[#6B7280]">
                You&apos;re about to mark <span className="font-medium text-[#2C2C2E]">{lostModal.lead?.client_name}</span> as Lost. This requires a reason for reporting and analytics.
              </p>
              <div>
                <Label className="text-xs text-[#6B7280] uppercase tracking-wide">Loss Reason <span className="text-[#C4727F]">*</span></Label>
                <Select
                  value={lostModal.lost_reason}
                  onValueChange={(v) => setLostModal(m => ({ ...m, lost_reason: v }))}
                >
                  <SelectTrigger className="mt-1 h-10 rounded-xl border-[#D4D0C8]" data-testid="lost-reason-select">
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {LOSS_REASONS.map(r => <SelectItem key={r} value={r} data-testid={`lost-reason-option-${r}`}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#6B7280] uppercase tracking-wide">Additional Notes (optional)</Label>
                <textarea
                  value={lostModal.lost_notes}
                  onChange={(e) => setLostModal(m => ({ ...m, lost_notes: e.target.value }))}
                  placeholder="Add context — e.g., what competitor, what was over budget, when client may return..."
                  className="mt-1 w-full h-24 px-4 py-2 bg-white border border-[#D4D0C8] rounded-xl text-sm focus:ring-[#C4727F]/20 focus:border-[#C4727F] outline-none resize-none"
                  data-testid="lost-notes-textarea"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setLostModal(null)}
                  className="flex-1 h-10 bg-white border border-[#D4D0C8] text-[#2C2C2E] rounded-full font-medium text-sm hover:bg-[#FAFAF8] transition-colors"
                  data-testid="lost-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={submitMarkLost}
                  disabled={!lostModal.lost_reason}
                  className="flex-1 h-10 bg-[#C4727F] text-white rounded-full font-medium text-sm hover:bg-[#A85B68] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="lost-confirm-btn"
                >
                  Mark as Lost
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FollowUpsSection({ lead, onRefresh }) {
  const [confirm, confirmDialog] = useConfirm();
  const todayStr = new Date().toISOString().slice(0, 10);
  const blank = { date: todayStr, channel: 'Call', outcome: 'Follow up', next_follow_up_date: '', feedback: '' };
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const items = [...(lead.follow_ups || [])].sort((a, b) =>
    (b.date || b.created_at || '').localeCompare(a.date || a.created_at || ''));
  const count = items.length;
  const last = items[0];
  const upcoming = items.map(f => f.next_follow_up_date).filter(Boolean).filter(d => d >= todayStr).sort()[0] || null;
  const decided = lead.status === 'Won' || lead.status === 'Lost';

  const reset = () => { setShowForm(false); setEditingId(null); setForm(blank); };

  const submit = async () => {
    if (!form.outcome && !form.feedback.trim()) { toast.error('Add an outcome or feedback'); return; }
    setSaving(true);
    try {
      if (editingId) await api.put(`/leads/${lead.id}/follow-up/${editingId}`, form);
      else await api.post(`/leads/${lead.id}/follow-up`, form);
      toast.success(editingId ? 'Interaction updated' : 'Interaction logged');
      reset();
      onRefresh();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const startEdit = (f) => {
    setEditingId(f.id);
    setForm({
      date: (f.date || '').slice(0, 10) || todayStr,
      channel: f.channel || 'Call',
      outcome: f.outcome || '',
      next_follow_up_date: (f.next_follow_up_date || '').slice(0, 10),
      feedback: f.feedback || f.notes || f.response || '',
    });
    setShowForm(true);
  };

  const remove = async (id) => {
    if (!(await confirm({ title: 'Delete interaction?', message: 'This follow-up record will be removed from the lead.', confirmLabel: 'Delete' }))) return;
    try { await api.delete(`/leads/${lead.id}/follow-up/${id}`); toast.success('Interaction deleted'); onRefresh(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="glass-card rounded-xl p-4" data-testid="lead-followups-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="section-heading text-sm font-semibold font-['Manrope']">Follow-ups &amp; Communication</h4>
          <p className="text-[11px] text-[#6B7280] mt-0.5" data-testid="followups-summary">
            {count === 0 ? 'No interactions logged yet'
              : `${count} interaction${count > 1 ? 's' : ''}${decided ? ` before ${lead.status}` : ''}${last ? ` · last ${fuFormatDate(last.date)}` : ''}`}
          </p>
        </div>
        {!showForm && (
          <button onClick={() => { setForm(blank); setEditingId(null); setShowForm(true); }} data-testid="add-followup-btn"
            className="flex items-center gap-1 text-xs text-[#3A8C8C] font-medium hover:underline shrink-0">
            <Plus size={13} /> Log Interaction
          </button>
        )}
      </div>

      {upcoming && !showForm && (
        <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-[#3A8C8C]/8 border border-[#3A8C8C]/15" data-testid="next-followup-banner">
          <Clock size={13} className="text-[#3A8C8C]" />
          <span className="text-xs text-[#2C2C2E]">Next follow-up: <span className="font-medium">{fuFormatDate(upcoming)}</span></span>
        </div>
      )}

      {showForm && (
        <div className="mb-3 p-3 rounded-xl bg-[#FAFAF8] border border-[#E8E6E1] space-y-3" data-testid="followup-form">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] text-[#6B7280] uppercase">Date</Label>
              <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })}
                className="mt-1 h-9 w-full rounded-lg border border-[#D4D0C8] text-sm px-2 bg-white outline-none focus:border-[#3A8C8C]" data-testid="followup-date" />
            </div>
            <div>
              <Label className="text-[10px] text-[#6B7280] uppercase">Channel</Label>
              <select value={form.channel} onChange={e => setForm({ ...form, channel: e.target.value })}
                className="mt-1 h-9 w-full rounded-lg border border-[#D4D0C8] text-sm px-2 bg-white outline-none focus:border-[#3A8C8C]" data-testid="followup-channel">
                {FU_CHANNELS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] text-[#6B7280] uppercase">Outcome</Label>
              <select value={form.outcome} onChange={e => setForm({ ...form, outcome: e.target.value })}
                className="mt-1 h-9 w-full rounded-lg border border-[#D4D0C8] text-sm px-2 bg-white outline-none focus:border-[#3A8C8C]" data-testid="followup-outcome">
                {FU_OUTCOMES.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] text-[#6B7280] uppercase">Next Follow-up</Label>
              <input type="date" value={form.next_follow_up_date} onChange={e => setForm({ ...form, next_follow_up_date: e.target.value })}
                className="mt-1 h-9 w-full rounded-lg border border-[#D4D0C8] text-sm px-2 bg-white outline-none focus:border-[#3A8C8C]" data-testid="followup-next-date" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] text-[#6B7280] uppercase">Feedback / Notes</Label>
            <textarea value={form.feedback} onChange={e => setForm({ ...form, feedback: e.target.value })}
              placeholder="What was discussed, client's response, concerns raised…"
              className="mt-1 w-full h-20 px-3 py-2 rounded-lg border border-[#D4D0C8] text-sm bg-white outline-none focus:border-[#3A8C8C] resize-none" data-testid="followup-feedback" />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={reset} className="px-3 py-1.5 rounded-lg text-xs text-[#6B7280] hover:bg-[#E8E6E1]">Cancel</button>
            <button onClick={submit} disabled={saving} className="px-4 py-1.5 rounded-lg bg-[#3A8C8C] text-white text-xs font-medium hover:bg-[#2E7A7A] disabled:opacity-50" data-testid="followup-save">
              {saving ? 'Saving…' : editingId ? 'Update' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {count > 0 ? (
        <div className="space-y-1.5">
          {items.map(f => {
            const Icon = fuChannelIcon(f.channel);
            const body = f.feedback || f.notes || f.response;
            return (
              <div key={f.id} className="group flex gap-3 p-2.5 rounded-lg hover:bg-[#FAFAF8] border border-transparent hover:border-[#E8E6E1] transition-colors" data-testid={`followup-${f.id}`}>
                <div className="w-8 h-8 rounded-full bg-[#3A8C8C]/10 flex items-center justify-center shrink-0">
                  <Icon size={14} className="text-[#3A8C8C]" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-[#2C2C2E]">{f.channel || 'Interaction'}</span>
                    {f.outcome && <Badge className={`text-[9px] px-1.5 py-0 ${fuOutcomeColor(f.outcome)}`}>{f.outcome}</Badge>}
                    <span className="text-[11px] text-[#6B7280]">{fuFormatDate(f.date)}</span>
                  </div>
                  {body && <p className="text-xs text-[#2C2C2E] mt-1 whitespace-pre-wrap">{body}</p>}
                  <div className="flex items-center gap-2 mt-1 text-[10px] text-[#6B7280]">
                    {f.created_by_name && <span>by {f.created_by_name}</span>}
                    {f.next_follow_up_date && <span className="text-[#3A8C8C]">· next {fuFormatDate(f.next_follow_up_date)}</span>}
                  </div>
                </div>
                <div className="flex items-start gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => startEdit(f)} title="Edit" data-testid={`followup-edit-${f.id}`} className="p-1 rounded hover:bg-[#E8E6E1] text-[#6B7280]"><Edit size={12} /></button>
                  <button onClick={() => remove(f.id)} title="Delete" data-testid={`followup-delete-${f.id}`} className="p-1 rounded hover:bg-[#C4727F]/10 text-[#C4727F]"><Trash2 size={12} /></button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (!showForm && <p className="text-xs text-[#6B7280] py-1">Track every call, meeting, and message with the client here.</p>)}

      {confirmDialog}
    </div>
  );
}

function LeadDetail({ lead, onClose, onStatusChange, onConvert, onDelete, onRefresh }) {
  const { user } = useAuth();
  const [confirm, confirmDialog] = useConfirm();
  const isAdmin = isOwner(user); // proposals are Owner-only
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(lead);
  const [showProposal, setShowProposal] = useState(false);
  const [editProposalId, setEditProposalId] = useState(null); // null = new proposal

  const updateLeadField = async (field, value) => {
    try {
      await api.put(`/leads/${lead.id}`, { [field]: value });
      onRefresh();
    } catch { toast.error('Update failed'); }
  };

  const openNewProposal = () => { setEditProposalId(null); setShowProposal(true); };
  const openEditProposal = (id) => { setEditProposalId(id); setShowProposal(true); };

  const reviseProposal = async (id) => {
    try {
      const res = await api.post(`/leads/${lead.id}/proposals/${id}/revise`);
      toast.success(`Created v${res.data.version} — edit and re-send`);
      await onRefresh();
      openEditProposal(res.data.id);
    } catch { toast.error('Failed to create revision'); }
  };

  const deleteProposal = async (id) => {
    if (!(await confirm({ title: 'Delete proposal?', message: 'This proposal and its saved layout will be permanently deleted. This cannot be undone.', confirmLabel: 'Delete' }))) return;
    try {
      await api.delete(`/leads/${lead.id}/proposals/${id}`);
      toast.success('Proposal deleted');
      onRefresh();
    } catch { toast.error('Delete failed'); }
  };

  if (showProposal && isAdmin) {
    return (
      <div className="glass-card rounded-2xl p-6 animate-fade-in" data-testid="lead-proposal-builder">
        <ProposalBuilder lead={lead} proposalId={editProposalId} onSave={() => onRefresh()} onClose={() => setShowProposal(false)} />
      </div>
    );
  }

  return (
    <div className="glass-card rounded-2xl p-6 animate-fade-in" data-testid="lead-detail">
      <div className="flex items-center justify-between mb-6">
        <button onClick={onClose} className="text-sm text-[#6B7280] hover:text-[#2C2C2E] transition-colors">&larr; Back to leads</button>
        <div className="flex gap-2">
          {isAdmin && (lead.status === 'Consultation' || lead.status === 'Proposal') && (
            <button onClick={openNewProposal} data-testid="open-proposal-builder-btn" className="bg-[#3A8C8C] text-white rounded-full px-4 py-2 text-xs font-medium hover:bg-[#2E7A7A] transition-colors flex items-center gap-1">
              <FileText size={14} /> Proposal Builder
            </button>
          )}
          {lead.status !== 'Won' && lead.status !== 'Lost' && (
            <button onClick={() => onConvert(lead.id)} data-testid="convert-lead-btn" className="bg-[#7DA68A] text-white rounded-full px-4 py-2 text-xs font-medium hover:bg-[#6D9478] transition-colors flex items-center gap-1">
              <ArrowRight size={14} /> Convert to Project
            </button>
          )}
          <button onClick={() => onDelete(lead.id)} data-testid="delete-lead-btn" className="p-2 rounded-lg hover:bg-[#C4727F]/10 text-[#C4727F] transition-colors"><Trash2 size={16} strokeWidth={1.5} /></button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Info */}
        <div className="md:col-span-2 space-y-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#3A8C8C]/10 flex items-center justify-center"><span className="text-xl font-semibold text-[#3A8C8C] font-['Manrope']">{lead.client_name?.[0]}</span></div>
            <div>
              <InlineEdit value={lead.client_name} onSave={v => updateLeadField('client_name', v)} className="text-xl font-['Manrope'] font-bold text-[#2C2C2E]" testId="inline-lead-name" />
              <div className="flex items-center gap-3 mt-1 text-xs text-[#6B7280]">
                <InlineEdit value={lead.phone} onSave={v => updateLeadField('phone', v)} className="text-xs text-[#6B7280]" placeholder="Add phone" testId="inline-lead-phone" />
                <InlineEdit value={lead.email} onSave={v => updateLeadField('email', v)} className="text-xs text-[#6B7280]" placeholder="Add email" testId="inline-lead-email" />
              </div>
            </div>
          </div>

          {/* Pipeline */}
          <div className="flex gap-1" data-testid="lead-pipeline-bar">
            {statusPipeline.filter(s => s !== 'Lost').map(s => (
              <button key={s} onClick={() => onStatusChange(lead.id, s)} className={`flex-1 py-2 text-xs font-medium text-center rounded-lg transition-all ${lead.status === s ? 'bg-[#3A8C8C] text-white' : 'bg-[#E8E6E1] text-[#6B7280] hover:bg-[#D4D0C8]'}`} data-testid={`pipeline-${s}`}>
                {s}
              </button>
            ))}
            <button
              onClick={() => onStatusChange(lead.id, 'Lost')}
              className={`px-3 py-2 text-xs font-medium rounded-lg transition-all ${lead.status === 'Lost' ? 'bg-[#C4727F] text-white' : 'bg-[#C4727F]/10 text-[#C4727F] hover:bg-[#C4727F]/20'}`}
              data-testid="pipeline-Lost"
              title="Mark as Lost"
            >
              ✕ Lost
            </button>
          </div>

          {lead.status === 'Lost' && (
            <div className="p-3 rounded-xl bg-[#C4727F]/8 border border-[#C4727F]/30" data-testid="lost-banner">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-[#C4727F] uppercase tracking-wider font-semibold">Lost Reason</span>
                    {lead.lost_date && <span className="text-[10px] text-[#6B7280]">· {new Date(lead.lost_date).toLocaleDateString()}</span>}
                  </div>
                  <p className="text-sm font-medium text-[#2C2C2E] mt-0.5" data-testid="lost-banner-reason">
                    {lead.lost_reason || 'Reason Not Specified'}
                  </p>
                  {lead.lost_notes && <p className="text-xs text-[#6B7280] mt-1 whitespace-pre-wrap">{lead.lost_notes}</p>}
                </div>
              </div>
            </div>
          )}

          {/* Details grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 rounded-xl bg-[#FAFAF8] border border-[#E8E6E1]"><span className="text-[10px] text-[#6B7280] uppercase">Source</span><div className="mt-0.5"><InlineEdit value={lead.source} onSave={v => updateLeadField('source', v)} type="select" options={sourceOptions} className="text-sm font-medium text-[#2C2C2E]" testId="inline-lead-source" /></div></div>
            <div className="p-3 rounded-xl bg-[#FAFAF8] border border-[#E8E6E1]"><span className="text-[10px] text-[#6B7280] uppercase">Project Type</span><div className="mt-0.5"><InlineEdit value={lead.project_type} onSave={v => updateLeadField('project_type', v)} type="select" options={typeOptions} className="text-sm font-medium text-[#2C2C2E]" testId="inline-lead-type" /></div></div>
            <div className="p-3 rounded-xl bg-[#FAFAF8] border border-[#E8E6E1]"><span className="text-[10px] text-[#6B7280] uppercase">Budget</span><div className="mt-0.5"><InlineEdit value={lead.budget} onSave={v => updateLeadField('budget', v)} type="number" displayValue={formatINR(lead.budget)} className="text-sm font-medium text-[#2C2C2E]" testId="inline-lead-budget" /></div></div>
            <div className="p-3 rounded-xl bg-[#FAFAF8] border border-[#E8E6E1]"><span className="text-[10px] text-[#6B7280] uppercase">Location</span><div className="mt-0.5"><InlineEdit value={lead.area_location} onSave={v => updateLeadField('area_location', v)} className="text-sm font-medium text-[#2C2C2E]" placeholder="Add location" testId="inline-lead-location" /></div></div>
          </div>

          <div className="p-4 rounded-xl bg-[#FAFAF8] border border-[#E8E6E1]"><span className="text-[10px] text-[#6B7280] uppercase">Notes</span><div className="mt-1"><InlineEdit value={lead.notes} onSave={v => updateLeadField('notes', v)} type="textarea" className="text-sm text-[#2C2C2E]" placeholder="Add notes" testId="inline-lead-notes" /></div></div>

          {/* Follow-ups & communication */}
          <FollowUpsSection lead={lead} onRefresh={onRefresh} />

          {/* Activity log */}
          {(lead.activity_log?.length > 0) && (
            <div className="p-4 rounded-xl bg-white border border-[#E8E6E1]" data-testid="lead-activity-log">
              <h4 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-3">Activity History</h4>
              <div className="space-y-3">
                {[...lead.activity_log].reverse().slice(0, 20).map((a) => (
                  <div key={a.id} className="flex gap-3 text-xs">
                    <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${a.to_status === 'Lost' ? 'bg-[#C4727F]' : a.to_status === 'Won' ? 'bg-[#7DA68A]' : 'bg-[#3A8C8C]'}`} />
                    <div className="flex-1">
                      <p className="text-[#2C2C2E]">
                        Lead moved to <span className="font-medium">{a.to_status}</span>
                        {a.from_status && <span className="text-[#6B7280]"> from {a.from_status}</span>}
                      </p>
                      {a.lost_reason && (
                        <p className="text-[#C4727F] mt-0.5"><span className="font-medium">Reason:</span> {a.lost_reason}</p>
                      )}
                      {a.lost_notes && <p className="text-[#6B7280] mt-0.5 whitespace-pre-wrap">Notes: {a.lost_notes}</p>}
                      <p className="text-[10px] text-[#6B7280] mt-1">
                        {a.user_name || 'Unknown'} · {a.timestamp ? new Date(a.timestamp).toLocaleString() : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar actions */}
        <div className="space-y-4">
          <div className="glass-card rounded-xl p-4 space-y-3">
            <h4 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Quick Actions</h4>
            {isAdmin && <button onClick={openNewProposal} data-testid="create-proposal-btn" className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl bg-[#3A8C8C]/5 border border-[#3A8C8C]/20 text-sm text-[#3A8C8C] font-medium hover:bg-[#3A8C8C]/10 transition-colors"><FileText size={14} strokeWidth={1.5} /> Create Proposal</button>}
            <button
              onClick={() => { if (!openWhatsApp(lead.phone, `Hi ${lead.client_name},`)) toast.error('No phone number on this lead'); }}
              className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl bg-[#FAFAF8] border border-[#E8E6E1] text-sm text-[#2C2C2E] hover:bg-[#E8E6E1]/50 transition-colors"><MessageSquare size={14} strokeWidth={1.5} className="text-[#3A8C8C]" /> WhatsApp Client</button>
            <button
              onClick={() => { if (!openEmail(lead.email, `Interior Story — ${lead.project_type || 'your'} project enquiry`)) toast.error('No email on this lead'); }}
              className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl bg-[#FAFAF8] border border-[#E8E6E1] text-sm text-[#2C2C2E] hover:bg-[#E8E6E1]/50 transition-colors"><Mail size={14} strokeWidth={1.5} className="text-[#3A8C8C]" /> Send Email</button>
            <button
              onClick={() => { if (!openCall(lead.phone)) toast.error('No phone number on this lead'); }}
              className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl bg-[#FAFAF8] border border-[#E8E6E1] text-sm text-[#2C2C2E] hover:bg-[#E8E6E1]/50 transition-colors"><Phone size={14} strokeWidth={1.5} className="text-[#3A8C8C]" /> Call</button>
          </div>
          {isAdmin && (
          <div className="glass-card rounded-xl p-4" data-testid="lead-proposals-panel">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Proposals</h4>
              {lead.proposals?.length > 0 && <span className="text-[10px] text-[#6B7280]">{lead.proposals.length}</span>}
            </div>
            {lead.proposals?.length > 0 ? (
              <div className="space-y-1">
                {[...lead.proposals]
                  .sort((a, b) => {
                    const ra = a.root_id || a.id, rb = b.root_id || b.id;
                    if (ra !== rb) return (a.created_at || '').localeCompare(b.created_at || '');
                    return (a.version || 1) - (b.version || 1);
                  })
                  .map(p => (
                  <div key={p.id} className="group flex items-center gap-2 py-2 border-b border-[#E8E6E1] last:border-0" data-testid={`proposal-row-${p.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <Badge className="bg-[#E8E6E1] text-[#6B7280] text-[9px] px-1.5 py-0 shrink-0">v{p.version || 1}</Badge>
                        <span className="text-sm text-[#2C2C2E] truncate">{p.title}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge className={`text-[9px] px-1.5 py-0 ${p.status === 'Approved' ? 'bg-[#7DA68A]/15 text-[#7DA68A]' : p.status === 'Sent' ? 'bg-[#3A8C8C]/15 text-[#3A8C8C]' : 'bg-[#C67D5B]/10 text-[#C67D5B]'}`}>{p.status}</Badge>
                        {p.total_amount ? <span className="text-[10px] text-[#6B7280]">₹{formatINR(p.total_amount)}</span> : null}
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => openEditProposal(p.id)} title="Edit" data-testid={`proposal-edit-${p.id}`} className="p-1.5 rounded-lg hover:bg-[#E8E6E1] text-[#6B7280]"><Edit size={13} strokeWidth={1.5} /></button>
                      <button onClick={() => reviseProposal(p.id)} title="Create revision (new version)" data-testid={`proposal-revise-${p.id}`} className="p-1.5 rounded-lg hover:bg-[#3A8C8C]/10 text-[#3A8C8C]"><Copy size={13} strokeWidth={1.5} /></button>
                      <button onClick={() => deleteProposal(p.id)} title="Delete" data-testid={`proposal-delete-${p.id}`} className="p-1.5 rounded-lg hover:bg-[#C4727F]/10 text-[#C4727F]"><Trash2 size={13} strokeWidth={1.5} /></button>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-[#6B7280]">No proposals yet</p>}
          </div>
          )}
        </div>
      </div>

      {confirmDialog}
    </div>
  );
}
